import { z } from 'zod';
import { PHONE_MIN_LENGTH } from '../constants';
import { isKnownTimeZone } from '../date';

export const contactSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email('Invalid email').max(100),
  phone: z.string().min(PHONE_MIN_LENGTH),
  zip_code: z.string().min(4),
  details: z.string().nullable(),
  consent: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the terms',
  }),
});

export type ContactInput = z.infer<typeof contactSchema>;

export const pluginInput = z
  .record(
    z.string().min(1),
    z.record(z.string().min(1), z.union([z.string(), z.number(), z.boolean()]))
  )
  .optional();

export const leadInput = z.object({
  session: z.string(),
  form: z.object({
    id: z.number(),
  }),
  campaign: z.object({
    id: z.number(),
    lead_type: z.object({
      id: z.number(),
      lead_type_name: z.string(),
    }),
    lead_source: z.object({
      id: z.number(),
    }),
    lead_score_baseline: z.number(),
    offer: z.object({
      id: z.union([z.number(), z.string()]),
    }),
  }),
  lead: z.object({
    answers: z.record(
      z.string().min(1),
      z.object({
        value: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1),
        score: z.array(z.number()).min(1),
      })
    ),
    contact: contactSchema,
    is_qualified: z.boolean(),
    is_duplicate: z.boolean(),
    score: z.number(),
  }),
  is_2fa_enabled: z.boolean(),
  analytics: z.looseObject({
    ip_address: z.string().nullable(),
    user_agent: z.string().nullable(),
    url: z.string().optional().nullable(),
  }),
  plugins: pluginInput,
});

export const leadSchema = z.object({
  id: z.number().int().positive().optional(),
  campaign_id: z.number().int(),
  lead_type_id: z.number().int(),
  session: leadInput,
});

export type LeadSchema = z.infer<typeof leadSchema>;

export type LeadInput = z.infer<typeof leadInput>;

export type Plugins = z.infer<typeof pluginInput>;

/** A single answer as emitted by the form engine (scalar or multi value). */
const leadV2Answer = z.object({
  value: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.union([z.string(), z.number(), z.boolean()])),
    z.null(),
  ]),
  score: z.array(z.number()),
});

/**
 * Input of the `submitFormLead` server action. The public form renderer knows only which
 * form it is and the answers the visitor gave; campaign, lead type and lead source are
 * resolved server-side from the form's claim section, so they are never trusted from
 * the client. Contact fields travel inside a `contact*` content block, question
 * answers inside `question` blocks — exactly the shape of `FormSubmitPayload.answers`.
 */
export const leadV2Input = z.object({
  form_id: z.number().int().positive(),
  is_qualified: z.boolean(),
  answers: z.array(
    z.object({
      step: z.number().int(),
      content: z.array(
        z.object({
          type: z.string(),
          answers: z.record(z.string(), leadV2Answer),
        })
      ),
    })
  ),
  analytics: z
    .object({
      session_id: z.string().optional().nullable(),
      url: z.string().optional().nullable(),
    })
    .optional(),
});

export type LeadV2Input = z.infer<typeof leadV2Input>;

/**
 * The visitor's zone is resolved from the ZIP code they gave, so it spans every zone in the
 * country — not the curated list an admin picks the office's from. Validating against the IANA
 * database the runtime already carries keeps a new or renamed zone from being rejected here.
 */
const ianaTimeZoneSchema = z.string().refine(isKnownTimeZone, {
  message: 'Unknown IANA time zone',
});

export const leadCallbackPayloadSchema = z.object({
  callback_on: z
    .string()
    .datetime({ offset: true })
    .refine((value) => new Date(value).getTime() > Date.now(), {
      message: 'Callback date must be in the future',
    }),
  tz: ianaTimeZoneSchema,
  notes: z.string(),
});

export type LeadCallbackPayload = z.infer<typeof leadCallbackPayloadSchema>;
